/**
 * FP3-030B — Admin-facing "Invite user" modal.
 *
 * Wraps the existing FP3-028 / FP3-029B6 invite-create endpoint with a
 * calm, scoped form. Visible only to admin family users; rendered
 * from `AdminAccess.jsx` ("Access · Invites & Requests").
 *
 * Field rules:
 *   - Provider type drives `provider_role_type`:
 *       SLPA / PTA / COTA   → assistant
 *       everything else     → primary
 *   - Supervising provider is REQUIRED for assistant invites and
 *     filtered to active primaries within the same supervision
 *     scope. Cross-discipline pairings still allowed (matches
 *     FP3-029B6D — the gate is supervision-relationship, not
 *     discipline; the backend may still emit a soft warning).
 *   - School / campus is optional for trial invites and required
 *     for school-team / district admins per backend rules; the
 *     backend enforces, the UI just surfaces the picker.
 *   - Org admins / admins are scoped to their own organization;
 *     super_admin can pick across all orgs but the simple MVP
 *     leaves the org_id blank → backend defaults to admin's org.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, Warning } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Provider type → (provider_role_type, assistant_type)
const PROVIDER_TYPES = [
  { value: "SLP",                         label: "SLP — Speech-Language Pathologist", role_type: "primary",   assistant_type: "" },
  { value: "OT",                          label: "OT — Occupational Therapist",        role_type: "primary",   assistant_type: "" },
  { value: "PT",                          label: "PT — Physical Therapist",            role_type: "primary",   assistant_type: "" },
  { value: "Special Education Teacher",   label: "Special Education Teacher",          role_type: "primary",   assistant_type: "" },
  { value: "School Psychologist",         label: "School Psychologist",                role_type: "primary",   assistant_type: "" },
  { value: "Counselor",                   label: "Counselor",                          role_type: "primary",   assistant_type: "" },
  { value: "BCBA",                        label: "BCBA",                               role_type: "primary",   assistant_type: "" },
  { value: "Behavior Specialist",         label: "Behavior Specialist",                role_type: "primary",   assistant_type: "" },
  { value: "SLPA",                        label: "SLPA — Speech-Language Pathology Assistant", role_type: "assistant", assistant_type: "SLPA" },
  { value: "COTA",                        label: "COTA — Certified Occupational Therapy Assistant", role_type: "assistant", assistant_type: "COTA" },
  { value: "PTA",                         label: "PTA — Physical Therapy Assistant",   role_type: "assistant", assistant_type: "PTA" },
  { value: "Other",                       label: "Other staff role",                   role_type: "primary",   assistant_type: "" },
];

const ACCESS_TYPES = [
  { value: "trial",      label: "Trial" },
  { value: "pilot",      label: "Pilot" },
  { value: "limited",    label: "Limited" },
  { value: "membership", label: "Full Membership" },
];

const ROLE_OPTIONS_BY_PERMISSION = {
  super_admin: [
    { value: "provider",  label: "Provider / staff" },
    { value: "admin",     label: "Admin" },
    { value: "org_admin", label: "Organization admin" },
  ],
  admin: [
    { value: "provider", label: "Provider / staff" },
    { value: "admin",    label: "Admin" },
  ],
  org_admin: [
    { value: "provider", label: "Provider / staff" },
    { value: "admin",    label: "Admin" },
  ],
};

export default function InviteUserModal({ open, onClose, onCreated, prefillOrganizationId = "", prefillIntendedRole = "" }) {
  const { user } = useAuth();
  const myPermission = user?.permission_role || "admin";
  const roleOptions = ROLE_OPTIONS_BY_PERMISSION[myPermission] || ROLE_OPTIONS_BY_PERMISSION.admin;

  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [schools, setSchools] = useState([]);
  // FP3-043 Phase A UI — super_admin must explicitly pick an
  // organization. Org-admins/admins never see this picker; they
  // inherit their own org server-side.
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState("");

  // Form state
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [intendedRole, setIntendedRole] = useState("provider");
  const [providerType, setProviderType] = useState("SLP");
  const [supervisingProviderId, setSupervisingProviderId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [accessType, setAccessType] = useState("trial");
  const [note, setNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  const reset = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    // FP3-037 correction — When the modal is opened from the Super
    // Admin "Invite Org Admin" flow, prefill intended_role and lock
    // organization scope on submit. The prop default keeps the
    // existing Access & Invites flow identical for org admins.
    setIntendedRole(prefillIntendedRole || "provider");
    setProviderType("SLP");
    setSupervisingProviderId("");
    setSchoolId("");
    setAccessType("trial");
    setNote("");
    setSendEmail(true);
    // FP3-043 Phase A UI — preselect prefill if super_admin came in
    // from a per-org context (Platform Dashboard row action,
    // /admin/access?as_org=X, etc). Otherwise force blank so the
    // operator has to commit.
    setOrganizationId(prefillOrganizationId || "");
  };

  useEffect(() => {
    if (!open) return;
    reset();
    let cancelled = false;
    (async () => {
      try {
        const [pr, sc, orgs] = await Promise.all([
          api.getAdminProviders().catch(() => ({ data: [] })),
          api.listSchools().catch(() => ({ data: [] })),
          // FP3-043 Phase A UI — Only super_admin needs the org list.
          // Other roles invite into their own org and never see this
          // picker.
          myPermission === "super_admin"
            ? api.listOrganizations().catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;
        setProviders(pr.data || []);
        setSchools(sc.data || []);
        // Stable sort by name so the picker reads predictably. Mongo's
        // own ordering is insertion order which surfaces older test
        // orgs first.
        const orgRows = (orgs.data || []).slice().sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
        setOrganizations(orgRows);
      } catch {
        // keep modal usable even if hydration fails
      }
    })();
    return () => {
      cancelled = true;
    };
    // myPermission is stable for the lifetime of the modal — useAuth
    // returns the same identity object across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillOrganizationId, myPermission]);

  // FP3-043 Phase A UI — School picker filters by the currently
  // selected (or pre-filled) organization for super_admin. For
  // org-admin / admin the backend defaults to their own org, so we
  // just show every school the user is already allowed to see via
  // /api/schools.
  const filteredSchools = useMemo(() => {
    const targetOrg =
      (myPermission === "super_admin" ? organizationId : "") || prefillOrganizationId;
    if (!targetOrg) return schools;
    return schools.filter((s) => s.organization_id === targetOrg);
  }, [schools, organizationId, prefillOrganizationId, myPermission]);

  // When the chosen org changes, clear the school choice so we
  // don't carry a stale school from another org.
  useEffect(() => {
    if (myPermission !== "super_admin") return;
    if (schoolId && !filteredSchools.some((s) => s.id === schoolId)) {
      setSchoolId("");
    }
  }, [filteredSchools, schoolId, myPermission]);

  const providerTypeMeta = useMemo(
    () => PROVIDER_TYPES.find((p) => p.value === providerType) || PROVIDER_TYPES[0],
    [providerType]
  );

  const isAssistant =
    intendedRole === "provider" && providerTypeMeta.role_type === "assistant";

  // Active primary providers for the supervisor picker. We don't
  // hard-filter by discipline (matches FP3-029B6D); the admin can
  // pair any primary with any assistant; backend will warn on
  // unusual pairings.
  const supervisorOptions = useMemo(() => {
    return providers.filter(
      (p) =>
        (p.provider_role_type || "primary") === "primary" &&
        (p.account_status || "active") !== "deactivated" &&
        (p.provider_status || "available") !== "inactive"
    );
  }, [providers]);

  // FP3-030C — School-driven email domain helper. When the admin
  // selects a school that has an `email_domain` set (e.g.
  // "lincoln.k12.us"), we surface that as an inline @suffix and
  // auto-resolve any bare-username input ("p.patel") into a full
  // address ("p.patel@lincoln.k12.us") at submit time. The admin
  // can still type a full email manually; we never double-append
  // when the input already contains an "@".
  const selectedSchool = useMemo(
    () => filteredSchools.find((s) => s.id === schoolId) || null,
    [schoolId, filteredSchools]
  );
  const schoolDomain = (selectedSchool?.email_domain || "").trim().toLowerCase();
  const emailHasAt = email.includes("@");
  const resolvedEmail = useMemo(() => {
    const trimmed = (email || "").trim();
    if (!trimmed) return "";
    if (trimmed.includes("@")) return trimmed.toLowerCase();
    if (schoolDomain) return `${trimmed.toLowerCase()}@${schoolDomain}`;
    return trimmed.toLowerCase();
  }, [email, schoolDomain]);
  // Basic email shape check on the resolved value.
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resolvedEmail);

  // FP3-043 Phase A UI — Super-admin invites are blocked client-side
  // (and server-side via the FP3-043 422 guard) unless an
  // organization is explicitly selected.
  const needsOrgChoice = myPermission === "super_admin";
  const effectiveOrgId = needsOrgChoice
    ? (organizationId || prefillOrganizationId || "")
    : prefillOrganizationId || "";
  const orgPickerInvalid = needsOrgChoice && !effectiveOrgId;

  const canSubmit =
    emailLooksValid &&
    firstName.trim() &&
    lastName.trim() &&
    intendedRole &&
    !orgPickerInvalid &&
    (intendedRole !== "provider" || providerType) &&
    (!isAssistant || supervisingProviderId);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        email: resolvedEmail,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        intended_role: intendedRole,
        provider_specialty: intendedRole === "provider" ? providerType : "",
        access_type: accessType,
        school_id: schoolId || "",
        // FP3-043 Phase A — Super-admin invites must carry an explicit
        // organization_id (the server rejects with 422 otherwise).
        // Other roles default to their own org via the backend's
        // `body.organization_id or admin.organization_id` fallback.
        organization_id: effectiveOrgId,
        send_email: !!sendEmail,
        note: note.trim(),
        provider_role_type:
          intendedRole === "provider" ? providerTypeMeta.role_type : "primary",
        assistant_type:
          intendedRole === "provider" ? providerTypeMeta.assistant_type : "",
        supervising_provider_id: isAssistant ? supervisingProviderId : "",
        // FP3-090 — plural canonical list on the invite payload.
        supervising_provider_ids:
          isAssistant && supervisingProviderId ? [supervisingProviderId] : [],
      };
      const res = await api.createInvite(payload);
      const sendStatus = res?.data?.send_status || "not_sent";
      if (sendStatus === "sent") {
        toast.success(`Invite sent to ${resolvedEmail}`);
      } else if (sendStatus === "preview_logged") {
        toast.success(`Invite created — preview email logged`);
      } else if (!sendEmail) {
        toast.success("Invite created (email not sent)");
      } else {
        toast.success("Invite created");
      }
      onCreated?.(res?.data);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        (typeof detail === "string" && detail) ||
        (detail && typeof detail === "object" && detail.message) ||
        "Could not create invite";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="invite-user-modal"
        className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <form onSubmit={submit}>
          <header className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#F1F2F4]">
            <div className="flex items-center gap-2 min-w-0">
              <EnvelopeSimple
                size={16}
                weight="duotone"
                className="text-[#1E3F33] shrink-0"
              />
              <DialogTitle className="text-[15px] font-semibold text-[#1F2937] heading-font">
                Invite user
              </DialogTitle>
            </div>
            {/* FP3-035D — Shadcn `DialogContent` already renders an
                absolute-positioned close X in the top-right corner.
                A second custom X here produced a duplicate-button
                bug, so we let the built-in handle close (escape +
                outside-click still work via the same primitive). */}
          </header>

          <div className="px-5 py-4 space-y-3.5">
            {/* FP3-043 Phase A UI — Organization picker. Super-admin
                only: org-admins/admins always invite into their own
                org and never see this control. */}
            {needsOrgChoice && (
              <Field
                label="Organization"
                required
                hint={
                  prefillOrganizationId
                    ? "Pre-selected from the org you opened this modal from. You can switch if needed."
                    : "Super Admin invites must be scoped to an organization."
                }
              >
                <select
                  data-testid="invite-user-organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  className={inputClasses}
                >
                  <option value="">— Select organization —</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                      {o.account_type ? ` · ${o.account_type.replace(/_/g, " ")}` : ""}
                    </option>
                  ))}
                </select>
                {orgPickerInvalid && (
                  <p
                    data-testid="invite-user-organization-error"
                    className="text-[10.5px] text-[#7A3A2C] mt-1 flex items-center gap-1"
                  >
                    <Warning size={10} weight="duotone" />
                    Pick an organization before sending the invite.
                  </p>
                )}
              </Field>
            )}

            {/* School / campus — placed first so the email-domain
                helper is set up before the email field is typed. */}
            <Field
              label="School / campus"
              hint={
                schoolDomain
                  ? `Staff at this school typically use @${schoolDomain}. Optional — required for school-team admins.`
                  : needsOrgChoice && !effectiveOrgId
                    ? "Select an organization above to load its schools."
                    : "Optional. Required for school-team admins."
              }
            >
              <select
                data-testid="invite-user-school"
                value={schoolId}
                onChange={(e) => setSchoolId(e.target.value)}
                className={inputClasses}
                disabled={needsOrgChoice && !effectiveOrgId}
              >
                <option value="">— None —</option>
                {filteredSchools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.email_domain ? ` · @${s.email_domain}` : ""}
                  </option>
                ))}
              </select>
            </Field>

            {/* Email — shows the school's email domain inline as a
                visible @suffix when set, so the admin can type just
                "p.patel" and we auto-resolve to
                "p.patel@lincoln.k12.us" on submit. Pasting a full
                email overrides the helper (we never double-append). */}
            <Field
              label="Email"
              required
              hint={
                schoolDomain && !emailHasAt && email.trim()
                  ? `Will send to: ${resolvedEmail}`
                  : schoolDomain && !email.trim()
                    ? `Type just the username — we'll add @${schoolDomain}.`
                    : "Type a full email address, or pick a school first to auto-append its domain."
              }
            >
              <div
                className={`flex items-center bg-white border border-[#E5E3DB] rounded-md focus-within:border-[#1E3F33] ${schoolDomain && !emailHasAt ? "" : ""}`}
              >
                <input
                  data-testid="invite-user-email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={
                    schoolDomain ? "p.patel" : "user@school.org"
                  }
                  className="flex-1 text-[12.5px] p-2 bg-transparent focus:outline-none"
                  autoFocus
                />
                {schoolDomain && !emailHasAt && (
                  <span
                    data-testid="invite-user-email-domain"
                    className="text-[12px] text-[#3F4756] bg-[#F1F2F4] px-2 py-2 border-l border-[#E5E3DB] select-none whitespace-nowrap"
                  >
                    @{schoolDomain}
                  </span>
                )}
              </div>
              {email.trim() && !emailLooksValid && (
                <p className="text-[10.5px] text-[#7A3A2C] mt-1 flex items-center gap-1">
                  <Warning size={10} weight="duotone" />
                  Enter a valid email address
                  {schoolDomain && !emailHasAt
                    ? ` (we'll append @${schoolDomain})`
                    : ""}
                  .
                </p>
              )}
            </Field>

            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" required>
                <input
                  data-testid="invite-user-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClasses}
                />
              </Field>
              <Field label="Last name" required>
                <input
                  data-testid="invite-user-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClasses}
                />
              </Field>
            </div>

            {/* Role */}
            <Field label="Role">
              <select
                data-testid="invite-user-role"
                value={intendedRole}
                onChange={(e) => setIntendedRole(e.target.value)}
                className={inputClasses}
              >
                {roleOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>

            {/* Provider type — only for provider role */}
            {intendedRole === "provider" && (
              <Field label="Provider type">
                <select
                  data-testid="invite-user-provider-type"
                  value={providerType}
                  onChange={(e) => {
                    setProviderType(e.target.value);
                    // Reset supervisor when toggling primary↔assistant
                    setSupervisingProviderId("");
                  }}
                  className={inputClasses}
                >
                  {PROVIDER_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {/* Supervising provider — only for assistants */}
            {isAssistant && (
              <Field label="Supervising provider" required>
                <select
                  data-testid="invite-user-supervising-provider"
                  value={supervisingProviderId}
                  onChange={(e) => setSupervisingProviderId(e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Select supervising provider…</option>
                  {supervisorOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                      {p.role ? ` · ${p.role}` : ""}
                    </option>
                  ))}
                </select>
                {!supervisorOptions.length && (
                  <p className="text-[10.5px] text-[#7A3A2C] mt-1 flex items-center gap-1">
                    <Warning size={10} weight="duotone" />
                    No primary providers yet. Invite a primary first.
                  </p>
                )}
              </Field>
            )}

            {/* Access type — FP3-035D: Super Admin only. Org admins
                inherit the org's existing plan; the backend stamps it
                on save. Hiding the field here keeps the modal calm
                and the duplicate-control bug fixed. */}
            {myPermission === "super_admin" && (
              <Field label="Access type">
                <select
                  data-testid="invite-user-access-type"
                  value={accessType}
                  onChange={(e) => setAccessType(e.target.value)}
                  className={inputClasses}
                >
                  {ACCESS_TYPES.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {/* Note */}
            <Field
              label="Note"
              hint="Optional. Stored on the invite for your records."
            >
              <textarea
                data-testid="invite-user-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder="e.g. Cross-coverage hire for spring semester"
                className={`${inputClasses} resize-none`}
              />
            </Field>

            {/* Send email checkbox */}
            <label className="flex items-center gap-2 text-[12px] text-[#3F4756] mt-1">
              <input
                data-testid="invite-user-send-email"
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="rounded border-[#E5E3DB]"
              />
              Send invite email immediately
            </label>
          </div>

          <footer className="flex justify-end gap-2 px-5 py-3 border-t border-[#F1F2F4] bg-[#FBFAF6] rounded-b-2xl">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onClose?.()}
              className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="invite-user-submit"
              size="sm"
              disabled={!canSubmit || submitting}
              className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
            >
              <EnvelopeSimple size={12} weight="duotone" className="mr-1" />
              {submitting ? "Sending…" : sendEmail ? "Send invite" : "Create invite"}
            </Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const inputClasses =
  "w-full text-[12.5px] border border-[#E5E3DB] rounded-md p-2 bg-white focus:outline-none focus:border-[#1E3F33]";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold flex items-center gap-1">
        {label}
        {required && <span className="text-[#7A3A2C]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && (
        <span className="text-[10.5px] text-[#9AA0A6] mt-1 block">{hint}</span>
      )}
    </label>
  );
}
